import React, { useState, useEffect, useRef } from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import {
  Box,
  Heading,
  SimpleGrid,
  Link,
  VStack,
  Container,
  Text,
  Button,
  Select,
  Flex,
  FormLabel,
} from '@chakra-ui/react';
import { ArrowForwardIcon } from '@chakra-ui/icons';
import { RichText } from '../RichText';

function PolicyPapers() {
  const policyPaperData = useStaticQuery(PAPER_QUERY);
  const [visibilePapers, setVisiblePapers] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('All Policy Papers');
  const [allVisibile, setAllVisible] = useState(false);
  const papersRef = useRef(null);
  const papersListRef = useRef(null);

  const { papers } = policyPaperData.data;

  useEffect(() => {
    setVisiblePapers(sortObjectsByDate(papers).slice(0, 3));
  }, [papers]);

  useEffect(() => {
    if (selectedCategory === 'All Policy Papers') {
      setVisiblePapers(sortObjectsByDate(papers).slice(0, 3));
    } else {
      const filteredPapers = papers.filter((paper) => paper.categories.includes(selectedCategory));
      setVisiblePapers(sortObjectsByDate(filteredPapers).slice(0, 3));
      papersRef.current.scrollIntoView({ behavior: 'smooth' });
    }
    setAllVisible(false);
  }, [selectedCategory, papers]);

  useEffect(() => {
    if (allVisibile) {
      // Set focus to the first link in the fourth paper
      const firstLink = papersListRef?.current?.children[3]?.querySelector('a');
      firstLink?.focus();
    }
  }, [allVisibile]);

  const handleClick = () => {
    if (selectedCategory === 'All Policy Papers') {
      setVisiblePapers(sortObjectsByDate(papers));
    } else {
      const filteredPapers = papers.filter((paper) => paper.categories.includes(selectedCategory));
      setVisiblePapers(sortObjectsByDate(filteredPapers));
    }
    setAllVisible(true);
  };

  // Split the categories into buttons which can be clicked to filter the papers
  const Categories = ({ categories }) => (
    <Flex gap={5} direction={{ base: 'column', md: 'row' }} alignItems='flex-start' flexWrap='wrap'>
      {categories.map((category, index) => (
        <Flex key={category + index} alignItems='center' width={{ base: '100%', md: 'auto' }}>
          <Button
            textTransform='uppercase'
            letterSpacing='0.5px'
            fontSize='0.875rem'
            variant='link'
            onClick={() => setSelectedCategory(category)}
            aria-label={`Filter papers by ${category}`}
            width={{ base: 'auto', md: 'auto' }}
            textAlign='left'
          >
            {category}
          </Button>
          {index < categories.length - 1 && (
            <Text display='inline' ml='5px'>
              /
            </Text>
          )}
        </Flex>
      ))}
    </Flex>
  );

  return (
    <Box
      as='section'
      ref={papersRef}
      scrollMarginTop={60}
      bg='zinc.100'
      padding={{ base: 30, xl: 60 }}
      mx='auto'
      my={30}
      maxWidth='container.lg'
    >
      <Container maxWidth='container.md' px={{ base: 0, xl: 30 }}>
        <Flex
          as='header'
          flexDirection={{ base: 'column', lg: 'row' }}
          align={{ base: 'flex-start', lg: 'center' }}
          justify={{ base: 'flex-start', lg: 'space-between' }}
          gap={10}
          mb={60}
        >
          <Box role='region' id='papersHeader' aria-live='polite'>
            <Text as='h2' textStyle='h2'>
              Policy Papers
            </Text>
            <Text>Filtered by {selectedCategory}</Text>
          </Box>
          <Box>
            <FormLabel htmlFor='category-filter'>Filter</FormLabel>
            <CategoryDropdown
              papers={papers}
              setSelectedCategory={setSelectedCategory}
              selectedCategory={selectedCategory}
              id='category-filter'
            />
          </Box>
        </Flex>
        <SimpleGrid columns='1' ref={papersListRef}>
          {visibilePapers.map(({ title, categories, url, summary, date, press_release }, index) => (
            <VStack
              key={title}
              align='left'
              flex='auto'
              pb={60}
              pl={30}
              borderLeft={index === visibilePapers.length - 1 ? 'none' : '1px dashed darkGray'}
              position='relative'
            >
              <Box
                _before={{
                  content: '""',
                  color: 'navy',
                  fontSize: '60px',
                  fontWeight: 'bold',
                  display: 'inline-block',
                  position: 'absolute',
                  left: 0,
                  top: '3px',
                  height: '15px',
                  width: '15px',
                  borderRadius: '50%',
                  background: 'navy',
                  transform: 'translateX(-50%)',
                }}
              >
                <Text fontSize='sm'>{formatDate(date)}</Text>
              </Box>
              <Categories categories={categories} />
              <Link href={url} color='navy'>
                <Heading as='h3' fontSize='2xl'>
                  {title}
                </Heading>
              </Link>
              <RichText fontWeight={300} value={summary} />
              {press_release && (
                <Link
                  href={press_release}
                  textTransform='uppercase'
                  letterSpacing='0.5px'
                  fontSize='0.875rem'
                  color='navy'
                  fontWeight={600}
                  target='_blank'
                >
                  View Press Release
                  <ArrowForwardIcon ml={2} mt='-3px' />
                </Link>
              )}
            </VStack>
          ))}
        </SimpleGrid>
      </Container>
      {!allVisibile && visibilePapers.length > 2 && (
        <Container maxWidth='container.md' px={{ base: 0, xl: 60 }}>
          <Button width='100%' variant='primary' onClick={() => handleClick()}>
            View All
          </Button>
        </Container>
      )}
    </Box>
  );
}

const CategoryDropdown = ({ papers, setSelectedCategory, selectedCategory, id }) => {
  const categories = [];

  papers.forEach((paper) => {
    paper.categories.forEach((category) => {
      const trimmedCategory = category.trim();
      if (!categories.includes(trimmedCategory)) {
        categories.push(trimmedCategory);
      }
    });
  });

  return (
    <Select
      value={selectedCategory}
      bg='white'
      size='lg'
      onChange={(event) => setSelectedCategory(event.target.value)}
      aria-controls='papersHeader'
      id={id}
      border='none'
    >
      <option value='All Policy Papers'>All Policy Papers</option>
      {categories.map((category) => (
        <option key={category} value={category}>
          {category}
        </option>
      ))}
    </Select>
  );
};

function formatDate(dateString) {
  if (!dateString) return '';
  const dateParts = dateString.split('-');
  const year = dateParts[0].substring(2); // Extract the last two digits of the year
  const month = parseInt(dateParts[1], 10);
  const day = parseInt(dateParts[2], 10);

  return `${month}/${day}/${year}`;
}

function sortObjectsByDate(objects) {
  objects.sort((a, b) => {
    const dateA = new Date(a.date);
    const dateB = new Date(b.date);
    return dateA - dateB;
  });
  return objects;
}

const PAPER_QUERY = graphql`
  query PAPERQUERY {
    data: allSanityPolicyPaper {
      papers: nodes {
        title
        summary: _rawSummary
        date
        categories
        url
        press_release
      }
    }
  }
`;

export default PolicyPapers;
